// 🛑 فرض کنید این توابع و آرایه‌ها از یک فایل utilities ایمپورت شده‌اند:
//frontend/context/PlayerContext.js
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'ogg', 'm4v'];
const getFileExtension = (url) => {
    if (!url) return '';
    try {
        const pathname = new URL(url).pathname;
        const parts = pathname.split('.');
        if (parts.length < 2) return '';
        return parts[parts.length - 1].toLowerCase();
    } catch (e) {
        const simpleParts = url.split('?')[0].split('.');
        if (simpleParts.length < 2) return '';
        return simpleParts[simpleParts.length - 1].toLowerCase();
    }
};
// --------------------------------------------------------------------------------------


import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';

const PlayerContext = createContext(undefined);

// 🛑 ۱. هوک سفارشی usePlayerContext (بدون تغییر)
export const usePlayerContext = () => {
    const context = useContext(PlayerContext);
    if (context === undefined) {
        throw new Error('usePlayerContext must be used within a PlayerProvider');
    }
    return context;
};

// 🛑 ۲. کامپوننت PlayerProvider
export const PlayerProvider = ({ children }) => {
    
    const [currentMedia, setCurrentMedia] = useState(null); 
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef(null); 

    // ----------------------------------------------------
    // توابع کنترل
    // ----------------------------------------------------

    // الف) متصل کردن AudioPlayer به Context (بدون تغییر)
    const registerAudioRef = (ref) => {
        audioRef.current = ref;
        console.log("Context: Audio ref REGISTERED successfully.");
    };
    
    // 🛑 تعریف closePlayer 
    const closePlayer = useCallback(() => {
        if (audioRef.current) {
            console.log("Context: Closing player. Pausing and resetting time.");
            audioRef.current.pause(); 
            audioRef.current.currentTime = 0; 
            audioRef.current.src = ''; // 💡 ریست کردن سورس
        }
        setIsPlaying(false); 
        setCurrentMedia(null); 
    }, []);
    
    
    // ب) شروع پخش یک رسانه جدید 
    const playMedia = useCallback((media) => {
        const audio = audioRef.current;

        // 🛑 گارد ۱: جلوگیری از لود شدن فایل ویدیویی در پلیر صوتی
        const extension = getFileExtension(media.url);
        if (VIDEO_EXTENSIONS.includes(extension)) {
            console.error("Context ERROR: Blocked attempt to load a VIDEO file in the Audio Player Context.");
            closePlayer(); 
            return; 
        }

        // 🛑 گارد ۲: بررسی اتصال رفرنس صوتی
        if (!audio) {
            console.error("CRITICAL ERROR: Audio Player reference is not registered. Cannot play media.");
            setCurrentMedia(media); 
            setIsPlaying(false);
            return; 
        }
        
        console.log("Context: playMedia called for URL:", media.url);

        const isSameMedia = media.url === audio.src;
        
        // 1. اگر رسانه جدیدی انتخاب شده است (اکنون می‌دانیم که صوتی است)
        if (!isSameMedia) { 
            console.log("Context: Setting NEW media, loading audio element.");
            
            // 🛑 تنظیم سورس صوتی جدید و بارگذاری
            audio.src = media.url; 
            audio.load(); // بارگذاری مدیا
            
            setCurrentMedia(media); 
            setIsPlaying(true); 

        } else if (isSameMedia && !isPlaying) {
            // 2. اگر همان رسانه است و متوقف شده، ادامه پخش بده
            console.log("Context: Resuming playback of SAME media.");
            audio.play().catch(error => console.error("خطا در playMedia (ادامه پخش):", error));
            // 🛑 FIX: چون play() می‌تواند موفق شود اما رویداد 'play' دیر شلیک شود، وضعیت را مستقیم آپدیت می‌کنیم.
            setIsPlaying(true); 

        } else if (isSameMedia && isPlaying) {
             // 3. اگر همان رسانه است و در حال پخش است، آن را متوقف کن 
             console.log("Context: Media already playing. Pausing.");
             audio.pause();
             // 🛑 FIX: چون pause() می‌تواند موفق شود اما رویداد 'pause' دیر شلیک شود، وضعیت را مستقیم آپدیت می‌کنیم.
             setIsPlaying(false);
        }

    }, [isPlaying, closePlayer]); 

    
    // ج) تغییر وضعیت پخش (Play/Pause) 
    const togglePlayPause = useCallback(() => {
        const audio = audioRef.current;
        if (!audio || !currentMedia) return;

        if (isPlaying) {
            console.log("Context: Toggling to PAUSE (User click).");
            audio.pause();
            // 🟢 FIX VITAL: به‌روزرسانی مستقیم وضعیت برای پاسخگویی سریع UI
            setIsPlaying(false); 
        } else {
            console.log("Context: Toggling to PLAY (User click).");
            audio.play().catch(error => {
                console.error("خطا در togglePlayPause (پخش):", error);
                // اگر پخش موفق نشد، وضعیت را به حالت اولیه برگردان
                setIsPlaying(false); 
            });
            // 🟢 FIX VITAL: به‌روزرسانی مستقیم وضعیت برای پاسخگویی سریع UI
            setIsPlaying(true); 
        }
    }, [isPlaying, currentMedia]);

    
    // ----------------------------------------------------
    // ۳. مدیریت پخش خودکار پس از بارگیری رسانه جدید (بدون تغییر)
    // ----------------------------------------------------
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio || !currentMedia) return;
        
        const handleLoadedMetadata = () => {
            console.log("Context: loadedmetadata fired. Attempting audio.play()");
            if (isPlaying) {
                audio.play().catch(error => {
                    console.warn("Context WARN: Playback failed (Autoplay blocked OR 404).", error);
                    setIsPlaying(false); 
                });
            }
        };

        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        
        return () => {
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        };
    }, [currentMedia, isPlaying]); 

    
    // ----------------------------------------------------
    // د) به‌روزرسانی وضعیت isPlaying بر اساس رویدادهای پلیر (بدون تغییر)
    // ----------------------------------------------------
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        // 💡 شنونده‌ها اکنون فقط وضعیت نهایی (واقعی) را تأیید می‌کنند،
        // اما دیگر تنها منبع تغییر وضعیت نیستند.
        const handlePlay = () => {
            console.log("Event: audio 'play' fired (Confirmed).");
            setIsPlaying(true);
        }
        const handlePause = () => {
            console.log("Event: audio 'pause' fired (Confirmed).");
            setIsPlaying(false);
        }

        const handleEnded = () => {
            console.log("Event: audio 'ended' fired.");
            setIsPlaying(false);
            closePlayer(); 
        }
        
        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('ended', handleEnded);


        return () => {
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [closePlayer]); 

    const value = {
        // وضعیت
        currentMedia,
        isPlaying,
        audioRef, 

        // توابع کنترل
        playMedia,
        togglePlayPause,
        registerAudioRef, 
        closePlayer, 
    };

    return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
};